import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Instagram, Twitter, Github, Facebook, Zap, X } from "lucide-react";
import { getAuthentications, deleteAuthentication } from "@/lib/api";

const providerIcons: Record<string, React.ReactNode> = {
  instagram: <Instagram className="inline w-4 h-4 mr-1" />,
  twitter: <Twitter className="inline w-4 h-4 mr-1" />,
  github: <Github className="inline w-4 h-4 mr-1" />,
  facebook: <Facebook className="inline w-4 h-4 mr-1" />,
  vercel: <Zap className="inline w-4 h-4 mr-1" />,
};

function getProviderIcon(provider: string) {
  return providerIcons[provider.toLowerCase()] || <Zap className="inline w-4 h-4 mr-1" />;
}

export default function Authentications() {
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["authentications"],
    queryFn: getAuthentications,
  });

  // Remove authentication
  const removeMutation = useMutation({
    mutationFn: async (id: number) => {
      return await deleteAuthentication(id);
    },
    onSuccess: () => {
      toast.success("Service removed");
      queryClient.invalidateQueries({ queryKey: ["authentications"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to remove service");
    },
  });

  // CSRF token for forms
  const [csrfToken, setCsrfToken] = React.useState<string>("");

  React.useEffect(() => {
    const meta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement;
    if (meta && meta.content) {
      setCsrfToken(meta.content);
    }
  }, []);

  if (isLoading) {
    return <div className="p-8">Loading...</div>;
  }
  if (isError) {
    return <div className="p-8 text-red-500">Error: {(error as Error).message}</div>;
  }

  return (
    <div className="max-w-2xl- mx-auto- mt-8">
      <Card className="p-6">
        <div className="mb-4">
          <div className="font-semibold mb-2">You have authorized the following services:</div>
          <div className="flex flex-wrap gap-2">
            {data.authentications && data.authentications.length > 0 ? (
              data.authentications.map((auth: any) => (
                <div
                  key={auth.id}
                  className="flex items-center border rounded px-3 py-1 bg-white text-black"
                >
                  {getProviderIcon(auth.provider)}
                  <span className="mr-2">{auth.provider_human}</span>
                  <button
                    className="ml-1 text-gray-500 hover:text-red-600"
                    onClick={() => removeMutation.mutate(auth.id)}
                    title="Remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))
            ) : (
              <span className="text-gray-500">No services authorized.</span>
            )}
          </div>
        </div>
        <div className="mb-2 font-semibold">Authorize new service:</div>
        <div className="flex flex-col gap-2 mb-4">
          {data.available_providers && data.available_providers.length > 0 ? (
            data.available_providers.map((provider: string) => (
              <form
                key={provider}
                action={`/users/auth/${provider}`}
                method="post"
                className="contents"
              >
                <input
                  type="hidden"
                  name="authenticity_token"
                  value={csrfToken}
                />
                <Button
                  type="submit"
                  variant="outline"
                  className="w-fit flex items-center"
                >
                  {getProviderIcon(provider)}
                  Sign in with {provider.charAt(0).toUpperCase() + provider.slice(1)}
                </Button>
              </form>
            ))
          ) : (
            <span className="text-gray-500">No new services available.</span>
          )}
        </div>
        <div className="mb-2">configure:</div>
        <div className="flex flex-col gap-2">
          <Button
            asChild
            className="w-56"
            variant="default"
          >
            <a
              href={data.github_app_configure_url}
              target="_blank"
              rel="noopener noreferrer"
              data-turbo="false"
            >
              configure GITHUB APP
            </a>
          </Button>

          <Button
            asChild
            className="w-56"
            variant="default"
          >
            <a
              href={data.vercel_url}
              target="_blank"
              rel="noopener noreferrer"
              data-turbo="false"
            >
              configure Vercel
            </a>
          </Button>

          <Button
            asChild
            className="w-56"
            variant="default"
          >
            <a
              href="/stripe_connect"
              data-turbo="false"
            >
              configure Stripe Connect
            </a>
          </Button>
        </div>

      </Card>
    </div>
  );
}
